import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import React, {
	CSSProperties,
	ElementType,
	FunctionComponent,
	PropsWithChildren,
	ReactNode,
} from "react";
import { Transition } from "react-transition-group";
import styles from "./FlexUniversalDrawer.module.css";

const DEFAULT_SIZE_OPEN = 256;
const DEFAULT_SIZE_CLOSED = 24;
const TRANSITION_DURATION = 300;

type DrawerSide = "left" | "right" | "top" | "bottom";

// TODO: cleanup code and comments and fix anti-pattern in this component if design-system does not come up with a suitable solution to the simultaneous panel drawers

interface DrawerConfig {
	/** Determines if the button is always visible */
	buttonAlwaysVisible?: boolean;

	/** Hides the open/close button */
	disabled?: boolean;

	/** Content to render in drawer when open */
	drawerNode: ReactNode;

	/** Content to render in drawer when closed (defaults to drawerNode if not provided) */
	drawerNodeWhenClosed?: ReactNode;

	/** If true, the drawer is completely hidden when closed */
	hideWhenClosed?: boolean;

	/** Indicates if the drawer is expanded */
	open: boolean;

	/** CSS class for open button */
	openButtonClassName?: string;

	/** Label for open button */
	openButtonLabel?: string;

	/** Callback when the open state changes */
	onToggleOpen?: (open: boolean) => void;

	/** Size of the drawer when closed (width or height) */
	sizeClosed?: number;

	/** Size of the drawer when open (width or height) */
	sizeOpen?: number;

	/** Callback when the drawer finishes its transition */
	onTransition?: () => void;
}

interface FlexUniversalDrawerProps extends PropsWithChildren {
	/** Map of drawer sides to their configurations */
	drawers: {
		left?: DrawerConfig;
		right?: DrawerConfig;
		top?: DrawerConfig;
		bottom?: DrawerConfig;
	};

	/** HTML tag to use for the component */
	component?: ElementType;

	/** Additional className for the root component */
	className?: string;

	/** Additional className for the main content */
	mainContentClassName?: string;

	/** className for the middle container */
	middleContainerClassName?: string;

	/** Additional styles for the root component */
	style?: CSSProperties;
}

const FlexUniversalDrawer: FunctionComponent<FlexUniversalDrawerProps> = (props) => {
	const { drawers, component: Component = "div", children, className, mainContentClassName, middleContainerClassName, style } = props;

	return (
		<Component
			className={clsx(styles.root, className)}
			style={{ display: "flex", flexDirection: "column", height: "100%", ...style }}
		>
			{/* Top Drawer */}
			{drawers.top && <SideDrawer config={drawers.top} side="top" />}
			{/* Middle Container */}
			<div className={clsx(styles.middleContainer, middleContainerClassName)}>
				{/* Left Drawer */}
				{drawers.left && <SideDrawer config={drawers.left} side="left" />}
				{/* Main Content */}
				<div className={clsx(styles.mainContent, mainContentClassName)}>{children}</div>
				{/* Right Drawer */}
				{drawers.right && <SideDrawer config={drawers.right} side="right" />}
			</div>
			{/* Bottom Drawer */}
			{drawers.bottom && <SideDrawer config={drawers.bottom} side="bottom" />}
		</Component>
	);
};

interface SideDrawerProps {
	side: DrawerSide;
	config: DrawerConfig;
}

const SideDrawer: FunctionComponent<SideDrawerProps> = ({ side, config }) => {
	const {
		open,
		drawerNode,
		drawerNodeWhenClosed,
		sizeOpen,
		sizeClosed,
		onToggleOpen,
		onTransition,
		openButtonClassName = "",
		openButtonLabel = "",
		buttonAlwaysVisible = false,
		disabled = false,
		hideWhenClosed = false,
	} = config;

	const isHorizontal = side === "left" || side === "right";
	const flexDirection =
		side === "left" ? "row" : side === "right" ? "row-reverse" : side === "top" ? "column" : "column-reverse";

	const maxSize = sizeOpen || DEFAULT_SIZE_OPEN;
	const minSize = hideWhenClosed ? 0 : sizeClosed ?? DEFAULT_SIZE_CLOSED;
	const transitionProperty = isHorizontal ? "width" : "height";

	const transitionStyles = {
		entering: { [transitionProperty]: `${maxSize}px` },
		entered: { [transitionProperty]: `${maxSize}px` },
		exiting: { [transitionProperty]: `${minSize}px` },
		exited: { [transitionProperty]: `${minSize}px` },
	  };

	const onClick = () => {
		onToggleOpen && onToggleOpen(!open);
		if (onTransition) {
			setTimeout(onTransition, TRANSITION_DURATION);
		}
	};

	// Button positioning
	const buttonPositionStyle: CSSProperties = { position: "absolute" };
	const halfIconSize = (sizeClosed ?? DEFAULT_SIZE_CLOSED) / 2;

	if (side === "left") {
		buttonPositionStyle.right = -halfIconSize;
		buttonPositionStyle.top = 16;
	} else if (side === "right") {
		buttonPositionStyle.left = -halfIconSize;
		buttonPositionStyle.top = 16;
	} else if (side === "top") {
		buttonPositionStyle.bottom = -halfIconSize;
		buttonPositionStyle.left = 16;
	} else {
		buttonPositionStyle.top = -halfIconSize;
		buttonPositionStyle.left = 16;
	}

	const buttonClassName = `${styles.button} ${(!open || buttonAlwaysVisible) ? styles.forceDisplay : ""
	} ${open ? "" : openButtonClassName}`;

	// Determine border class for the divider
	const borderClass =
		side === "left"
			? styles.borderLeft
			: side === "right"
				? styles.borderRight
				: side === "top"
					? styles.borderTop
					: styles.borderBottom;

	const drawerContainerStyle: CSSProperties = {
		display: "flex",
		flexDirection,
		position: "relative",
		flexShrink: 0,
		[isHorizontal ? "width" : "height"]: open,
	};

	return (
		<div className={borderClass} style={drawerContainerStyle}>
			<Transition in={open} timeout={TRANSITION_DURATION}>
				{(state) => (
					<div
						className={styles.drawer}
						style={{
							display: "flex",
							[transitionProperty]: transitionStyles[state][transitionProperty],
							transition: `${transitionProperty} ${TRANSITION_DURATION}ms`,
							overflow: "hidden",
							flexShrink: 0,
						}}
					>
						{state !== "exited" && state !== "exiting" ? (
							<div
								style={{
									flexGrow: 1,
									display: "flex",
									flexDirection: "column",
									[transitionProperty]: maxSize,
								}}
							>{drawerNode}</div>
						) : (
							(drawerNodeWhenClosed || drawerNode)
						)}
					</div>
				)}
			</Transition>
			{!disabled && (
				<IconButton
					aria-label={open ? "collapse" : "expand"}
					className={buttonClassName}
					onClick={onClick}
					style={buttonPositionStyle}
				>
					{getButtonIcon(side, open)}
					{!open && openButtonLabel && (
						<span className={styles.buttonLabel}>{openButtonLabel}</span>
					)}
				</IconButton>
			)}
		</div>
	);
};

/** Helper function to get the appropriate icon based on the side and open state */
const getButtonIcon = (side: DrawerSide, open: boolean) => {
	if (side === "left") {
		return open ? <ChevronLeftIcon /> : <ChevronRightIcon />;
	} else if (side === "right") {
		return open ? <ChevronRightIcon /> : <ChevronLeftIcon />;
	} else if (side === "top") {
		return open ? <ExpandLessIcon /> : <ExpandMoreIcon />;
	} else {
		return open ? <ExpandMoreIcon /> : <ExpandLessIcon />;
	}
};

export default FlexUniversalDrawer;
